import { call, takeEvery, race, take } from "redux-saga/effects"
import { createActions } from "reduxsauce"
import { get, post, put, patch, del, downloadFile } from "api"
import { sendQuery } from "api/graphql"
import { v4 as uuidv4 } from "uuid"

export const { Types, Creators } = createActions({
  get: ["url", "resolve", "reject", "abortSignal", "requestId"],
  post: ["url", "payload", "resolve", "reject", "abortSignal", "requestId"],
  put: ["url", "payload", "resolve", "reject", "abortSignal", "requestId"],
  patch: ["url", "payload", "resolve", "reject", "abortSignal", "requestId"],
  delete: ["url", "payload", "resolve", "reject", "abortSignal", "requestId"],
  graphql: ["account", "query", "variables", "resolve", "reject", "abortSignal", "requestId"],
  download: ["url", "resolve", "reject"],
  abort: ["requestId"],
}, {
  prefix: "ompnt/modules/apiWrapper/",
})

function* getSaga({ url, resolve, reject, abortSignal, requestId }) {
  try {
    if (abortSignal) {
      const { result, aborted } = yield race({
        result: call(get, url, undefined, abortSignal),
        aborted: take(action =>
          action.type === Types.ABORT
          && action.payload.requestId === requestId,
        ),
      })

      if (aborted) {
        throw new Error("Request was aborted")
      }

      yield call(resolve, result)
    } else {
      const result = yield call(get, url)
      yield call(resolve, result)
    }
  } catch (error) {
    if (error.message === "Request was aborted") {
      return
    }
    yield call(reject, error)
  }
}

function* postSaga({ url, payload, resolve, reject, abortSignal }) {
  try {
    const result = yield call(post, url, payload, undefined, abortSignal)
    yield call(resolve, result)
  } catch (error) {
    yield call(reject, error)
  }
}

function* putSaga({ url, payload, resolve, reject, abortSignal }) {
  try {
    const result = yield call(put, url, payload, undefined, abortSignal)
    yield call(resolve, result)
  } catch (error) {
    yield call(reject, error)
  }
}

function* patchSaga({ url, payload, resolve, reject, abortSignal }) {
  try {
    const result = yield call(patch, url, payload, abortSignal)
    yield call(resolve, result)
  } catch (error) {
    yield call(reject, error)
  }
}

function* deleteSaga({ url, payload, resolve, reject, abortSignal }) {
  try {
    const result = yield call(del, url, payload, abortSignal)
    yield call(resolve, result)
  } catch (error) {
    yield call(reject, error)
  }
}

function* downloadSaga({ url, resolve, reject }) {
  try {
    yield call(downloadFile, url)
    yield call(resolve)
  } catch (error) {
    yield call(reject, error)
  }
}

export function* graphqlSaga({ account, query, variables, resolve, reject, abortSignal }) {
  try {
    const result = yield call(sendQuery, query, variables, account, false, abortSignal)
    yield call(resolve, result)
  } catch (error) {
    yield call(reject, error)
  }
}

export function* saga() {
  yield takeEvery(Types.GET, getSaga)
  yield takeEvery(Types.POST, postSaga)
  yield takeEvery(Types.PUT, putSaga)
  yield takeEvery(Types.PATCH, patchSaga)
  yield takeEvery(Types.DELETE, deleteSaga)
  yield takeEvery(Types.GRAPHQL, graphqlSaga)
  yield takeEvery(Types.DOWNLOAD, downloadSaga)
}

// Helper function to create an AbortController with a unique request ID
export const createAbortController = () => {
  const controller = new AbortController()
  const requestId = uuidv4()

  return {
    controller,
    requestId,
    signal: controller.signal,
  }
}
